import React, { useState } from "react";
import {
  TextField,
  FormGroup,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CustomerDTO, SiteDTO } from "@beable/roster-backend-client-v1";
import customerService from "../../../services/CustomerService";
import { getStyles } from "./styles";

interface FindSiteDialogProps {
  handleRowClick: (site: SiteDTO) => void;
  onClose: () => void;
  open: boolean;
  customers: CustomerDTO[];
}


export default function FindSiteDialog({
  handleRowClick,
  open,
  onClose,
  customers
}: FindSiteDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [siteMatch, setSiteMatch] = useState<SiteDTO>();
  const [searchError, setSearchError] = useState("");

  const theme = useTheme();
  const styles = getStyles(theme);


  const doSearch = async () => {
    setSearchError("");
    let results: SiteDTO | undefined;

    if (!Number(searchTerm)) {
      results = await customerService.findSiteBySubdomain(searchTerm);
    }
    else {
      results = await customerService.findSiteById(Number(searchTerm));
      if (results) results.customerName = customers.find(c => c.id === results?.customerId)?.name;
    }
    setSiteMatch(results);

    if (!results) {
      setSearchError("No matching site found.");
    }
  };
  const handleKeyPress = (event: any) => {
    if (event.key === "Enter") {
      doSearch();
    }
  };

  const handleTableRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    e.persist();
    e.stopPropagation();

    handleRowClick(siteMatch!);
    doClose();
  };

  const doClose = () => {
    setSiteMatch(undefined);
    setSearchError("");
    setSearchTerm("");
    onClose();
  };

  return (
    <Dialog
      aria-labelledby="admin-modal-title"
      aria-describedby="admin-modal-description"
      open={open}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") {
          doClose();
        }
      }}
      sx={{
        overflow: "scroll",
      }}
      maxWidth="sm"
      fullWidth
    >
      <div>
        <DialogTitle id="search-modal-title">Search For Site</DialogTitle>
        <DialogContent dividers sx={{ minHeight: "300px" }}>
          <FormGroup>
            <Grid container>
              <Grid size={10}>
                <TextField
                  label="Enter a subdomain or site id"
                  variant="outlined"
                  id="filterText"
                  sx={styles.search}
                  slotProps={
                    {
                      input: {
                        value: searchTerm,
                      }
                    }
                  }
                  onChange={(e: any) => {
                    setSearchTerm(e.target.value);
                  }}
                  onKeyPress={handleKeyPress}
                />
              </Grid>
              <Grid
                container
                direction="row"
                size={2}
                sx={{
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                <Button
                  variant="contained"
                  aria-label="search-button"
                  color="primary"
                  sx={{ width: 50 }}
                  onClick={() => doSearch()}
                  disabled={!searchTerm}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </FormGroup>
          {siteMatch ? (
            <TableContainer component={Paper} sx={{ marginTop: "10px" }}>
              <Table aria-label="customer site table" sx={styles.table}>
                <TableHead>
                  <TableRow>
                    <TableCell align="left" sx={{ fontWeight: "bold" }}>
                      Customer Name
                    </TableCell>
                    <TableCell align="left" sx={{ fontWeight: "bold" }}>
                      Site Subdomain
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: "bold" }}
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow
                    id={siteMatch.customerId}
                    key={siteMatch.customerId}
                    onClick={handleTableRowClick}
                  >
                    <TableCell align="left">
                      {siteMatch.customerName}
                    </TableCell>
                    <TableCell align="left">
                      {siteMatch.subdomain}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" align="center">
              {searchError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            aria-label="close-button"
            color="primary"
            sx={{ width: 50 }}
            onClick={() => doClose()}
          >
            Close
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
}
